import React, { useEffect, useRef, useState } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { Resizable } from 're-resizable';

import CancelIcon from '@mui/icons-material/Cancel';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  styled,
  Typography,
} from '@mui/material';

import { RootState } from '../../app/rootReducer';
import { getAllTasks } from '../../features/tasks/slice/taskListSlice';
import { TaskTypes } from '../../features/tasks/types/taskTypes';
import useAnalytics from '../../useAnalytics';
import { Resource } from '../constants/resource';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

const MinDrawerWidth = 800;

export const ExternalLink = ({
  externalLink,
  setExternalLink,
  title,
}: {
  externalLink: string;
  setExternalLink: (link: string | undefined) => void;
  title?: string;
}) => {
  const timer = useRef<number>();
  const [minDrawerWidth, setMinDrawerWidth] = useState(MinDrawerWidth);
  const [drawerWidth, setDrawerWidth] = useState(MinDrawerWidth);
  const notesTask = useSelector(state =>
    getAllTasks(state.taskList).find(t => t.taskType === TaskTypes.NOTES),
  );
  const { logEvent } = useAnalytics();

  useEffect(() => {
    if (externalLink !== undefined) {
      timer.current = new Date().getTime();
    }
    return () => {
      const timeSpent = new Date().getTime() - timer.current!;
      logEvent('Time spent on the attachment', {
        timeSpent,
        title,
        externalLink,
        flush: true,
      });
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const pageWidth = window.innerWidth;
      const inputElement = document.getElementById(notesTask!.id.toString());
      if (inputElement) {
        const inputXPosition = inputElement.getBoundingClientRect().right;
        setMinDrawerWidth(pageWidth - inputXPosition);
      }
    };
    if (notesTask) {
      window.addEventListener('resize', handleResize);
      handleResize();
    }
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [notesTask]);

  return (
    <DrawerContainer
      open={externalLink !== undefined}
      anchor="right"
      variant="persistent"
      transitionDuration={{ enter: 0, exit: 0 }}
      onClose={() => setExternalLink(undefined)}
      PaperProps={{ style: { width: `${drawerWidth}px` } }}
    >
      <ResizableDrawer
        minWidth={minDrawerWidth}
        size={{
          height: '100vh',
          width: '100%',
        }}
        enable={{ left: true }}
        onResize={(_, __, elementRef) => {
          setDrawerWidth(Math.max(elementRef.offsetWidth, minDrawerWidth));
        }}
      >
        <DialogTitle>
          <DrawerTitleContainer>
            {title && <Typography variant="h6">{title}</Typography>}
            <div>
              <IconButton
                onClick={() => window.open(externalLink, '_blank')}
                title={Resource.OPEN_NEW_TAB}
              >
                <LaunchIcon />
              </IconButton>
              <IconButton
                onClick={() => setExternalLink(undefined)}
                title={Resource.CLOSE}
              >
                <CancelIcon />
              </IconButton>
            </div>
          </DrawerTitleContainer>
        </DialogTitle>

        <Divider />
        {externalLink && <iframe src={externalLink}></iframe>}
      </ResizableDrawer>
    </DrawerContainer>
  );
};

const DrawerContainer = styled(Drawer)(() => ({
  '& iframe': {
    width: '100%',
    flex: 1,
    display: 'block',
    border: 'none',
  },
}));

const ResizableDrawer = styled(Resizable)({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const DrawerTitleContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 15px',
  alignItems: 'center',
});
